import { useEffect, useState } from "react";
import UserAvatar from "../Forms/UserAvatar";
import { API_ROUTES } from "../../../config/api";
import{apiFetch} from "../../../config/apiFetch";
import theme from "../../../styles/theme";
import Tag from "../Information/Tag";
import Button from "../Forms/Button";
import ProjectUserManage from "../../defined/project_manage/ProjectUserManage";

/**
 * Composant séparé pour éviter le démontage/remontage des avatars
 * à chaque re-render de CardProject.
 */
const ProjectUsersAvatars = ({ projectId, refreshKey  }) => {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchUsers = async () => {
      try {
        const res = await apiFetch(API_ROUTES.projectUsers.byProject(projectId), {
          signal: controller.signal,
        });

        if (!res.ok) throw new Error("Erreur chargement avatars");

        const data = await res.json();
        setUsers(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Erreur avatars projet :", err);
      }
    };

    fetchUsers();
    return () => controller.abort();
  }, [projectId, refreshKey]);

  if (!users.length) {
    return (
      <div
        style={{
          minHeight: 36,
          marginBottom: theme.spacing.md,
          marginTop: theme.spacing.sm,
        }}
      />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: theme.spacing.xs,
        marginBottom: theme.spacing.md,
        marginTop: theme.spacing.sm,
      }}
    >
      {users.slice(0, 6).map((u) => (
        <UserAvatar key={u.userId} name={`${u.firstName} ${u.lastName}`} size={28} />
      ))}

      {users.length > 6 && (
        <span style={{ fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary }}>
          +{users.length - 6}
        </span>
      )}
    </div>
  );
};

const CardProject = ({
  id,
  name,
  description,
  sector,
  location,
  status,
  currency,
  archived,
  userCount,
  admin,
  onEdit,
  isSelected,
  onRefresh,
}) => {
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [membersCount, setMembersCount] = useState(userCount || 0);
  const [admins, setAdmins] = useState(admin || []);
  const [refreshKey, setRefreshKey] = useState(0);

  /**
   * Charge dynamiquement les utilisateurs du projet pour :
   * - actualiser le compteur
   * - identifier les administrateurs réels
   */
  useEffect(() => {
    const controller = new AbortController();

    const fetchMembers = async () => {
      try {
        const res = await apiFetch(API_ROUTES.projectUsers.byProject(id), {
          signal: controller.signal,
        });

        if (!res.ok) throw new Error("Erreur chargement membres projet");

        const data = await res.json();

        setMembersCount(data.length);

        const projectAdmins = data
          .filter((u) => u.roleInProject === "Administrator")
          .map((u) => `${u.firstName} ${u.lastName}`);

        setAdmins(projectAdmins);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Erreur chargement utilisateurs projet :", err);
      }
    };

    fetchMembers();
    return () => controller.abort();
  }, [id, isManageOpen]);

  return (
    <>
      {/* --- Carte principale du projet --- */}
      <div
        style={{
          border: `1px solid ${theme.colors.borders.default}`,
          borderRadius: theme.borders.radius.xl,
          padding: theme.spacing.md,
          margin: theme.spacing.md,
          backgroundColor: theme.colors.background.surface,
          boxShadow: theme.shadows.sm,
          transition: "all 0.2s",
          cursor: "default",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.boxShadow = theme.shadows.md;
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.boxShadow = theme.shadows.sm;
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {/* Statut du projet */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: theme.spacing.xs, fontWeights: theme.fontWeights.ultraLight }}>
          {status && (
            <Tag
              label={status}
              color={theme.statusColors.projectStatus?.[status]?.bg ?? theme.colors.background.muted}
              textColor={theme.statusColors.projectStatus?.[status]?.text ?? theme.colors.text.default}
            />
          )}
        </div>

        {/* Nom du projet */}
        <div
          style={{
            fontSize: theme.fontSizes.xl,
            fontWeight: theme.fontWeights.xl,
            color: theme.colors.text.corelia,
            marginBottom: theme.spacing.xs,
          }}
        >
          {name}
        </div>

        {/* Secteur et localisation */}
        <div
          style={{
            fontSize: theme.fontSizes.sm,
            color: theme.colors.text.primary,
            marginBottom: theme.spacing.xs,
          }}
        >
          {sector && (
            <span style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.secondary }}>
              {sector}
            </span>
          )}
          {location && <span> • {location}</span>}
        </div>

        {/* Description */}
        {description && (
          <div
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.text.body,
              marginBottom: theme.spacing.sm,
            }}
          >
            {description}
          </div>
        )}

        {/* Informations sur les membres */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: theme.fontSizes.xs,
            color: theme.colors.text.body,
            marginBottom: theme.spacing.md,
          }}
        >
          {/* Compteur d'utilisateurs */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <img src="/assets/icones/users.svg" alt="Utilisateurs" style={{ height: 16, width: 16 }} />
            <span>User(s): {membersCount}</span>
          </div>

          {/* Administrateurs */}
          {admins.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <img
                src="/assets/icones/chield.svg"
                alt="Admin"
                style={{
                  height: 16,
                  width: 16,
                  marginTop: 1,
                  opacity: 0.8,
                }}
              />
              <span
                style={{
                  color: theme.colors.text.body,
                  fontSize: theme.fontSizes.xs,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "300px",
                }}
                title={admins.join(", ")}
              >
                Admin(s): {admins.join(", ")}
              </span>
            </div>
          )}
        </div>

        {/* --- Avatars des membres --- */}
        <ProjectUsersAvatars projectId={id} refreshKey={refreshKey}/>

        {/* --- Boutons d’action --- */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            marginTop: theme.spacing.md,
            marginBottom: theme.spacing.xs,
            gap: theme.spacing.md,
          }}
        >
          <Button variant="blue" onClick={onEdit} disabled={!isSelected}>
            Edit
          </Button>

          <Button
            variant="blue"
            onClick={() => setIsManageOpen(true)}
            disabled={!isSelected}
          >
            Manage users
          </Button>
        </div>
      </div>

      {/* --- Modale gestion des utilisateurs --- */}
      <ProjectUserManage
        isOpen={isManageOpen}
        onClose={() => {setIsManageOpen(false); setRefreshKey((v) => v + 1);}}
        projectId={id}
        onRefresh={onRefresh}
      />
    </>
  );
};

export default CardProject;